import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  DialogContent,
  FormControlLabel,
  Grid,
  Stack,
  Toolbar,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { StatusCodesHelper } from "../../../models/StatusCodesHelper";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { RoutesBuilder } from "../../../models/RoutesBuilder";
import { AlertDialog } from "../../AlertDialog";
import { strings } from "../../../localization/LocalizedStrings";
import { SearchBar } from "../../SearchBar";
import { useSearchDiscogsReleases } from "../../../hooks/discogs/useSearchDiscogsReleases";
import { IDiscogsRelease } from "../../../hooks/discogs";
import { DiscogsReleaseGridItem } from "../DiscogsReleaseGridItem";
import { useAddAlbumFromDiscogsRelease } from "../../../hooks/albums/useAddAlbumFromDiscogsRelease";
import { IAlbum } from "../../../hooks/albums";
import { toast } from "react-toastify";
import { ToolbarBox } from "../../toolbar/ToolbarBox";
import { ToolbarRawButton } from "../../toolbar/ToolbarRawButton";
import { UserRole } from "../../../hooks/users";
import { useUser } from "../../../hooks/session";
import { QrScanner } from "@yudiel/react-qr-scanner";
import { ContentUnavailableNotice } from "../../attributes/ContentUnavailableNotice";
import { Firebase } from "../../../services/Firebase";
import { ProductPurchasesDialog } from "../../products/ProductPurchasesDialog";
import { ProductType } from "../../../hooks/products/interfaces";
import { DataStorage } from "../../../models/DataStorage";
import { useCheckDiscogsAccess } from "../../../hooks/discogs/useCheckDiscogsAccess";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const BarcodeScannerDialog: FunctionComponent<IBarcodeScannerDialogProps> = ({
  isOpen,
  setIsOpen,
  onNewAlbum
}) => {
  const [currentUser] = useUser.useState();
  const searchDiscogsReleases = useSearchDiscogsReleases();
  const { addAlbumFromDiscogsRelease } = useAddAlbumFromDiscogsRelease();
  const [releases, setReleases] = useState<IDiscogsRelease[]>([]);
  const [shouldUploadImage, setShouldUploadImage] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isBackCamera, setIsBackCamera] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [noResultsText, setNoResultsText] = useState("");
  const { checkDiscogsAccess } = useCheckDiscogsAccess(
    setAlertMessage,
    setIsAlertOpen,
    setIsLoading,
    () => {}
  );
  const theme = useTheme();
  const performSearch = async (query: string) => {
    if (query == "") return;
    // Remove leading zeroes
    query = query.replace(/^0+/, "");
    setNoResultsText("");
    setIsLoading(true);
    const { status, body } = await searchDiscogsReleases(query);
    if (StatusCodesHelper.isSuccessful(status)) {
      setReleases(body);
      if (!body || body.length == 0) {
        setNoResultsText(searchText);
      }
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  const addAlbum = async (release: IDiscogsRelease) => {
    setIsLoading(true);
    const { status, body } = await addAlbumFromDiscogsRelease({
      releaseId: release.id,
      shouldUploadImage
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      toast.success(strings.album_created);
      Firebase.shared.logFirebaseEvent("new_album_manual", {
        user_id: currentUser.uuid,
        album_id: body.uuid
      });
      DataStorage.resetAttributeData();
      onNewAlbum(body);
      setIsOpen(false);
    } else if (StatusCodesHelper.isLimitReached(status)) {
      setAlertMessage(body.message);
      setIsPurchaseAlertOpen(true);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (!isOpen || currentUser?.role == "guest") return;
    checkDiscogsAccess().then();
  }, [isOpen]);
  const handleClose = async () => {
    if (isLoading) return;
    setIsOpen(false);
  };
  return (
    <PiperDialog
      fullWidth
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ style: { height: "100%" } }}
    >
      <Toolbar>
        <h3>{strings.option_scan_barcode}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          href={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.scanBarcode()}`}
          target={"_blank"}
          tooltip={strings.help}
          iconType={IconType.Help}
        />
        <ToolbarButton
          loading={isLoading}
          onClick={handleClose}
          tooltip={strings.close}
          iconType={IconType.Close}
        />
      </Toolbar>
      <DialogContent>
        <Stack spacing={2} sx={{ height: "100%" }}>
          <Stack direction={"row"} spacing={1}>
            <ToolbarButton
              tooltip={strings.open_camera}
              iconType={IconType.PhotoCamera}
              highlighted={isCameraOpen}
              onClick={() => setIsCameraOpen(state => !state)}
            />
            <SearchBar
              className={styles.searchBar}
              onSearchRequested={text => setSearchText(text)}
              onSearchDismissed={() => setSearchText("")}
              forceThisText={searchText}
            />
            <ToolbarButton
              tooltip={strings.option_search_albums}
              iconType={IconType.TravelExplore}
              disabled={searchText == ""}
              onClick={() => performSearch(searchText)}
            />
          </Stack>
          <FormControlLabel
            sx={{ width: "100%" }}
            disabled={currentUser?.role != UserRole.premium}
            control={
              <Checkbox
                checked={shouldUploadImage}
                onChange={() => setShouldUploadImage(state => !state)}
              />
            }
            label={
              <Stack>
                <p>{strings.discogs_should_upload_image}</p>
                <p
                  className={styles.checkboxHelpText}
                  style={{ color: theme.palette.primary.subtitle }}
                >
                  {currentUser?.role == UserRole.premium
                    ? strings.discogs_should_upload_image_help
                    : strings.discogs_should_upload_image_non_premium}
                </p>
              </Stack>
            }
          />
          <Card className={styles.gridCard}>
            {releases && releases.length > 0 && (
              <Grid container spacing={2}>
                {releases.map(release => (
                  <DiscogsReleaseGridItem
                    key={release.id}
                    release={release}
                    disabled={isLoading}
                    onClick={() => addAlbum(release)}
                  />
                ))}
              </Grid>
            )}
            <ContentUnavailableNotice
              isLoading={isLoading}
              items={releases}
              emptyIconType={IconType.QRCode2Add}
              emptyTitle={strings.scan_barcode_title}
              emptyDescription={strings.scan_barcode_description}
              searchText={noResultsText}
              canAdd={false}
            />
          </Card>
        </Stack>
      </DialogContent>
      <PiperDialog maxWidth={"md"} open={isCameraOpen} onClose={() => setIsCameraOpen(false)}>
        <Toolbar>
          <h3>{strings.camera_title}</h3>
          <Box flexGrow={1} />
          <ToolbarBox>
            <ToolbarRawButton
              tooltip={strings.switch_camera}
              iconType={IconType.CameraSwitch}
              highlighted={!isBackCamera}
              onClick={() => setIsBackCamera(state => !state)}
            />
          </ToolbarBox>
          <ToolbarButton
            tooltip={strings.close}
            iconType={IconType.Close}
            onClick={() => setIsCameraOpen(false)}
          />
        </Toolbar>
        <QrScanner
          onDecode={result => {
            setSearchText(result);
            performSearch(result).then();
            setIsCameraOpen(false);
          }}
          onError={undefined}
          constraints={{ facingMode: isBackCamera ? "environment" : "user" }}
        />
      </PiperDialog>
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUMS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </PiperDialog>
  );
};

export interface IBarcodeScannerDialogProps {
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  onNewAlbum: (newAlbum: IAlbum) => void;
}
